import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { reviews, faqs } from '@/mocks/comics';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  // Using a static form URL for the contact form
  const formUrl = 'https://readdy.ai/api/form/d9pcmm2ftmtnfcfh0kl0';

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    const urlEncoded = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt') {
        urlEncoded.append(key, value as string);
      }
    });

    fetch(formUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: urlEncoded.toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        if (res.ok) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError('提交失败，请稍后再试。');
        }
      })
      .catch(() => {
        setFormStatus('error');
        setFormError('网络错误，请稍后再试。');
      });
  };

  const breadcrumbItems = [{ label: '首页', href: '/' }, { label: '关于我们' }];

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于叮咚漫画网 - 韩漫免费阅读平台',
          description: '叮咚漫画网提供韩漫免费阅读服务，包括韩国漫画在线和高清韩漫大全，汇聚优质漫画资源。',
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 md:pt-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Hero */}
            <section className="mt-4 mb-12 text-center" aria-label="关于我们">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">
                关于叮咚漫画网
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                叮咚漫画网是专业的韩漫免费阅读平台，致力于为广大漫画爱好者提供优质的韩国漫画在线浏览服务和高清韩漫大全资源。
              </p>
            </section>

            {/* Brand Story */}
            <section className="mb-12 max-w-4xl mx-auto" aria-label="品牌故事">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center font-heading">
                品牌故事
              </h2>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  叮咚漫画网成立于2020年，自创立之初便专注于韩漫免费阅读领域。我们深知韩漫爱好者对于高质量漫画资源的需求，因此投入大量精力收集和整理优质的韩国漫画在线内容。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  经过多年发展，叮咚漫画网已经积累了丰富的漫画资源库，涵盖恋爱、奇幻、动作、悬疑、恐怖等多个分类，能够满足不同读者的口味偏好。我们坚持每日更新，确保用户能够第一时间获取最新的漫画章节。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  未来，叮咚漫画网将继续深耕漫画阅读领域，不断优化用户体验，让每一位漫画爱好者在我们的平台上都能享受到舒适愉快的阅读时光。作为高清韩漫大全的代表平台，我们将持续为韩漫爱好者带来更多惊喜。
                </p>
              </div>
            </section>

            {/* Stats */}
            <section className="mb-12" aria-label="数据统计">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2 font-heading">12+</div>
                  <p className="text-xs md:text-sm text-foreground-600">漫画分类</p>
                </div>
                <div className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="text-3xl md:text-4xl font-bold text-accent-600 mb-2 font-heading">5000+</div>
                  <p className="text-xs md:text-sm text-foreground-600">漫画资源</p>
                </div>
                <div className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2 font-heading">100万+</div>
                  <p className="text-xs md:text-sm text-foreground-600">月活跃用户</p>
                </div>
                <div className="text-center p-6 bg-background-100 rounded-lg">
                  <div className="text-3xl md:text-4xl font-bold text-accent-600 mb-2 font-heading">99.9%</div>
                  <p className="text-xs md:text-sm text-foreground-600">服务稳定性</p>
                </div>
              </div>
            </section>

            {/* User Reviews */}
            <section className="mb-12" aria-label="用户口碑">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center font-heading">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reviews.map((review, index) => (
                  <div
                    key={index}
                    className="bg-background-100 rounded-lg p-5 border border-background-200"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={review.avatar}
                        alt={review.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{review.name}</p>
                        <p className="text-xs text-foreground-500">{review.date}</p>
                      </div>
                      <div className="flex items-center gap-0.5 ml-auto text-accent-500">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-xs"></i>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">
                      {review.comment}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ & Contact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              {/* FAQ */}
              <section aria-label="常见问题">
                <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">
                  常见问题
                </h2>
                <script type="application/ld+json">
                  {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: faqs.map((faq) => ({
                      '@type': 'Question',
                      name: faq.question,
                      acceptedAnswer: {
                        '@type': 'Answer',
                        text: faq.answer,
                      },
                    })),
                  })}
                </script>
                <div className="space-y-2">
                  {faqs.map((faq, index) => (
                    <details key={index} className="group bg-background-100 rounded-lg border border-background-200">
                      <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                        <h4 className="text-sm font-medium text-foreground-900 font-heading pr-4">
                          {faq.question}
                        </h4>
                        <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0 text-sm"></i>
                        <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0 text-sm"></i>
                      </summary>
                      <div className="px-4 pb-3">
                        <p className="text-sm text-foreground-600">{faq.answer}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* Contact Form */}
              <section aria-label="联系我们">
                <h2 className="text-xl font-bold text-foreground-900 mb-4 font-heading">
                  联系我们
                </h2>
                <div className="bg-background-100 rounded-lg p-5 border border-background-200">
                  <p className="text-sm text-foreground-600 mb-5">
                    如果您有任何问题或建议，欢迎通过以下方式联系我们，我们将尽快回复您。
                  </p>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="honeypot-wrapper" aria-hidden="true">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" readOnly />
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1.5">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1.5">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1.5">
                          留言
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400 resize-none transition-colors"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                      </div>
                      <button
                        type="submit"
                        className="w-full py-2.5 text-sm bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors font-medium whitespace-nowrap cursor-pointer"
                      >
                        发送留言
                      </button>
                    </div>

                    {formStatus === 'success' && (
                      <div className="mt-4 p-3 bg-accent-100 text-accent-700 text-sm rounded-lg text-center">
                        感谢您的留言，我们会尽快回复！
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg text-center">
                        {formError}
                      </div>
                    )}
                  </form>

                  <div className="mt-5 pt-4 border-t border-background-200 space-y-2">
                    <p className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-mail-line text-primary-500"></i>
                      contact@dingdongmanhua.com
                    </p>
                    <p className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-customer-service-line text-primary-500"></i>
                      在线客服：工作日 9:00 - 18:00
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}